// ============================================
// ConverseTech Frontend - Landing Page
// ============================================

import { useNavigate } from 'react-router-dom';
import { Mic, BarChart3, Users, Trophy } from 'lucide-react';
import {
  SignInButton,
  SignUpButton,
  SignedIn,
  SignedOut,
  UserButton,
} from '@clerk/clerk-react';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white">
      {/* Header */}
      <header className="container mx-auto px-4 py-6 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Mic className="w-8 h-8 text-primary-600" />
          <span className="text-xl font-bold text-gray-900">ConverseTech</span>
        </div>
        <nav className="flex items-center gap-4">
          <SignedOut>
            <SignInButton mode="modal">
              <button data-testid="sign-in-button" className="btn-secondary">
                Sign In
              </button>
            </SignInButton>
            <SignUpButton mode="modal">
              <button data-testid="sign-up-button" className="btn-primary">
                Get Started
              </button>
            </SignUpButton>
          </SignedOut>
          <SignedIn>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-primary mr-2"
            >
              Dashboard
            </button>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
        </nav>
      </header>

      {/* Hero Section */}
      <section data-testid="landing-hero" className="container mx-auto px-4 py-20 text-center">
        <h1 className="text-5xl font-bold text-gray-900 mb-6 text-balance">
          Master Technical English with AI Coaching
        </h1>
        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto text-balance">
          Train your technical communication skills with real-time AI feedback.
          Perfect for interviews, presentations, and technical discussions.
        </p>
        <div className="flex gap-4 justify-center">
          <SignedOut>
            <SignUpButton mode="modal">
              <button data-testid="hero-cta" className="btn-primary text-lg px-8 py-3">
                Start Free Training
              </button>
            </SignUpButton>
          </SignedOut>
          <SignedIn>
            <button
              data-testid="hero-dashboard"
              onClick={() => navigate('/dashboard')}
              className="btn-primary text-lg px-8 py-3"
            >
              Go to Dashboard
            </button>
          </SignedIn>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          4 Training Modes
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard
            icon={<Mic className="w-8 h-8" />}
            title="Conversation"
            description="Practice fluency with natural conversation"
          />
          <FeatureCard
            icon={<BarChart3 className="w-8 h-8" />}
            title="Technical Explanation"
            description="Structure your technical explanations"
          />
          <FeatureCard
            icon={<Users className="w-8 h-8" />}
            title="Mock Interview"
            description="Prepare for technical interviews"
          />
          <FeatureCard
            icon={<Trophy className="w-8 h-8" />}
            title="DET Simulation"
            description="Practice Duolingo English Test tasks"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to improve your English?
          </h2>
          <p className="text-primary-100 mb-8 max-w-xl mx-auto">
            Join thousands of engineers who are improving their technical
            communication skills with ConverseTech.
          </p>
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="bg-white text-primary-600 px-8 py-3 rounded-lg font-medium hover:bg-primary-50 transition-colors">
                Get Started Free
              </button>
            </SignUpButton>
          </SignedOut>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 text-center text-gray-500">
        <p>&copy; 2026 ConverseTech. All rights reserved.</p>
      </footer>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="card p-6 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-full mb-4">
        {icon}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}
